"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Program Benefits 1029 — BDU "Why Choose Bharathidasan University Online?".
 *
 * Port of BDU v1 `components/Degree/KeyBenefits/`: a tinted full-bleed background
 * image, centred heading with a rule under it, and a wrapping row of
 * icon / title / description cards.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 *
 * ROW HEIGHT — the reference measures every card title, groups them by `offsetTop`
 * (i.e. by visual row) and applies the tallest title height in each row as a
 * `min-height`, so descriptions line up across a row even when titles wrap to a
 * different number of lines. That behaviour is reproduced here.
 */

interface BenefitRow {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  meta_title?: string;
  meta_sub_title?: string;
  meta_title_color?: string;
  meta_sub_title_color?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramBenefits1029Data {
  metadata?: BenefitRow[];
  title?: string;
  title_start_color?: string;
  /** Rule under the heading */
  gradient_color_1?: string;
  /** Desktop / mobile background art */
  image?: string;
  mobile_image?: string;
  image_alt?: string;
  /** Flat tint laid over the background image */
  section_overlay_rgba?: string;
  status?: boolean;
}

const ProgramBenefits1029 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as
    | ProgramBenefits1029Data
    | undefined;

  const {
    title = "",
    title_start_color = "#FFFFFF",
    gradient_color_1 = "#FFFFFF",
    image = "",
    mobile_image = "",
    section_overlay_rgba = "rgba(14, 40, 57, 0.87)",
  } = sectionData || {};

  const benefits = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  // Mirror the reference's desktop/mobile background swap at 768px.
  const [bgImage, setBgImage] = useState(image || mobile_image || "");
  useEffect(() => {
    const pick = () => {
      if (window.innerWidth < 768 && mobile_image) setBgImage(mobile_image);
      else if (image) setBgImage(image);
      else setBgImage(mobile_image || "");
    };
    pick();
    window.addEventListener("resize", pick);
    return () => window.removeEventListener("resize", pick);
  }, [image, mobile_image]);

  // Per-row tallest title height, keyed by offsetTop.
  const titleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [rowHeights, setRowHeights] = useState<Record<number, number>>({});

  const measure = useCallback(() => {
    const rows: Record<number, HTMLDivElement[]> = {};
    titleRefs.current.forEach((el) => {
      if (!el) return;
      const top = el.offsetTop;
      if (!rows[top]) rows[top] = [];
      rows[top].push(el);
    });
    const next: Record<number, number> = {};
    Object.entries(rows).forEach(([top, els]) => {
      next[Number(top)] = Math.max(...els.map((el) => el.offsetHeight));
    });
    setRowHeights(next);
  }, []);

  useEffect(() => {
    const id = window.setTimeout(measure, 50);
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("resize", measure);
    };
  }, [measure, benefits]);

  if (!sectionData?.status || benefits.length === 0) return null;

  return (
    <div
      // The reference only sets `md:w-full`, which is a no-op there because the
      // section is a block element. This app's <main> is `flex flex-col items-center`,
      // so the width has to be explicit at every size, not just from `tab:` up.
      className="relative flex h-full w-full items-center justify-center bg-cover bg-center bg-no-repeat px-[25px] py-8 tab:px-[88px] tab:py-11"
      style={{
        backgroundImage: `linear-gradient(0deg, ${section_overlay_rgba} 0%, ${section_overlay_rgba} 100%)${
          bgImage ? `, url('${bgImage}')` : ""
        }`,
        backgroundColor: "lightgray",
      }}
    >
      <div className="absolute inset-0 size-full opacity-75" />
      <div className="relative z-[11] flex flex-col items-center justify-center gap-10 tab:w-full">
        <div className="flex w-full flex-col items-center justify-center gap-[18px] tab:gap-5">
          <h2
            className="text-center text-[24px] font-bold tab:text-[28px]"
            style={{ color: title_start_color }}
          >
            {title}
          </h2>
          <div
            className="h-px w-[200px] tab:h-[3px] tab:w-[442px]"
            style={{ backgroundColor: gradient_color_1 }}
          />
        </div>

        <div className="flex flex-col flex-wrap items-center justify-center gap-10 tab:flex-row tab:items-start tab:gap-x-[60px] tab:gap-y-10">
          {benefits.map((item, idx) => {
            const top = titleRefs.current[idx]?.offsetTop ?? 0;
            const minHeight = rowHeights[top];

            return (
              <div
                key={item.id ?? idx}
                className="flex h-full flex-col items-center justify-center gap-[10px] tab:w-[calc(33.33%-40px)] tab:gap-2"
              >
                <div className="flex w-[58px] items-center justify-center tab:h-[62px]">
                  {item.meta_image && (
                    <Image
                      src={item.meta_image}
                      alt={item.meta_alt_image || "Icon"}
                      width={40}
                      height={40}
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="flex w-full flex-col items-center gap-[10px] text-center tab:gap-2">
                  <div
                    className="flex w-full items-center justify-center"
                    style={{ minHeight }}
                    ref={(el) => {
                      titleRefs.current[idx] = el;
                    }}
                  >
                    <p
                      className="text-center text-[14px] font-[600] leading-[150%] tab:text-lg/[28px]"
                      style={{ color: item.meta_title_color || "#F4F217" }}
                    >
                      {item.meta_title}
                    </p>
                  </div>
                  <p
                    className="text-[14px] font-[400] tab:text-base"
                    style={{ color: item.meta_sub_title_color || "#FFFFFF" }}
                  >
                    {item.meta_sub_title}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgramBenefits1029;
